// re.rejouis.fr — QUESTIONNAIRE D'ÉTAT (porté du proto, branché sur l'estimation réelle).
// Règle métier : « fonctionnel » est une PORTE, pas un grade décoté.
// Non fonctionnel → on ne reprend pas en crédit → bascule vers le don.
import { useEffect, useState } from 'react';
import { EcranHeader, Icons, Thumb, Sticker, Parcours, Illus, PastilleBoost, PrixBarre } from '../components/ui.jsx';
import { etatDepuisReponses, estimerPourEtat, estElectronique, ETAT_DESC } from '../lib/grades.js';
import { finPrio } from '../lib/prio.js';
import { fmtEuro } from '../lib/format.js';
import { creditFicheProvisoire } from '../lib/pricing.js';
import { pctVirement, montantVirement } from '../lib/rachat.js';
import { disponibiliteVirement } from '../lib/api.js';

// « 2026-11-22 » → « 22 novembre ».
const dateLongue = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

// Barre de progression légère.
function Progres({ total, courant }) {
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= courant ? 'var(--rouge)' : 'var(--stone-pale)' }} />
      ))}
    </div>
  );
}

export default function Etat({ go, produit, remplace, onAjouter, client, panier = [] }) {
  const [rep, setRep] = useState({});
  const [etape, setEtape] = useState(0);
  // Rachat en euros : sur un modèle activé au lab, le client choisit ici, jouet par jouet,
  // entre bon d'achat (défaut) et virement (70 % du crédit). Pour changer ensuite, il
  // retire le jouet du panier et refait l'estimation.
  const pctVir = produit && !produit.provisoire ? pctVirement(produit) : 0;
  const [paiement, setPaiement] = useState('bon');
  const [dispo, setDispo] = useState(null);
  useEffect(() => {
    if (!pctVir) return undefined;
    let vivant = true;
    disponibiliteVirement().then((d) => { if (vivant) setDispo(d); }).catch(() => { if (vivant) setDispo({ disponible: false }); });
    return () => { vivant = false; };
  }, [pctVir]);

  if (!produit) {
    // Entrée directe sans produit (ex. deep-link non résolu) → retour catalogue.
    return (
      <div style={{ padding: '60px 18px', textAlign: 'center' }}>
        <p style={{ color: 'var(--stone)', fontSize: 14 }}>Produit introuvable.</p>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => go('catalogue', { focus: true })}>Chercher un modèle</button>
      </div>
    );
  }

  // Formulation adaptée : électronique (allumage/moteur/charge) vs autres
  // produits — godes, plugs, lingerie, accessoires… (cf. estElectronique).
  const elec = estElectronique(produit);

  // Étapes dynamiques (traces sautées si jamais servi / scellé).
  const etapes = [];
  etapes.push(elec
    ? {
      cle: 'fonctionne', q: 'Il s’allume et tout répond ?', s: 'Moteur, vitesses, charge : un essai rapide suffit.',
      reponses: [
        { v: 'oui', t: 'Oui, tout fonctionne', ic: 'marche' },
        { v: 'non', t: 'Non, il a un défaut', s: 'On ne reprend pas en crédit, mais tu peux en faire don.', ic: 'oups' },
      ],
    }
    : {
      cle: 'fonctionne', q: 'Il est en parfait état ?', s: 'Aucune déchirure, casse ou pièce manquante.',
      reponses: [
        { v: 'oui', t: 'Oui, aucun défaut', ic: 'bouclier' },
        { v: 'non', t: 'Non, il a un défaut', s: 'On ne reprend pas en crédit, mais tu peux en faire don.', ic: 'oups' },
      ],
    });
  if (rep.fonctionne !== 'non') {
    etapes.push({
      cle: 'servi', q: 'Il est encore sous scellé ?', s: 'Blister ou emballage d’origine jamais ouvert.',
      reponses: [
        { v: 'oui', t: 'Non, l’emballage est ouvert', s: 'On l’évalue comme reconditionné', ic: 'ouvert' },
        { v: 'scelle', t: 'Oui, encore scellé', s: 'Jamais ouvert, considéré comme neuf', ic: 'scelle' },
      ],
    });
    if (rep.servi === 'oui') {
      etapes.push({
        cle: 'traces', q: 'Des traces d’usure ?', s: 'Regarde la matière de près.',
        reponses: [
          { v: 'aucune', t: 'Aucune', s: 'Rien à l’œil nu', ic: 'traces-aucune' },
          { v: 'legeres', t: 'Légères marques', s: elec ? 'Micro-marques, silicone impeccable' : 'Micro-marques, matière impeccable', ic: 'traces-legeres' },
          { v: 'marquees', t: 'Marques visibles', s: elec ? 'Rayures ou chocs nets' : 'Usure, taches ou accrocs nets', ic: 'traces-marquees' },
        ],
      });
    }
    etapes.push(elec
      ? {
        cle: 'access', q: 'Chargeur et accessoires ?', s: 'Boîte d’origine, câble, pochette…',
        reponses: [
          { v: 'complet', t: 'Tout est là', ic: 'complet' },
          { v: 'incomplet', t: 'Il manque des choses', s: 'Pas grave, ça ajuste juste l’estimation', ic: 'incomplet' },
        ],
      }
      : {
        cle: 'access', q: 'Emballage et accessoires ?', s: 'Boîte d’origine, pochette, notice…',
        reponses: [
          { v: 'complet', t: 'Tout est là', ic: 'complet' },
          { v: 'incomplet', t: 'Il manque des choses', s: 'Pas grave, ça ajuste juste l’estimation', ic: 'incomplet' },
        ],
      });
  }

  // Non fonctionnel → on coupe court et on oriente vers le don.
  // Retour = on revient à l'étape 1 (la question d'état), pas plus loin :
  // il faut donc remettre etape à 0 ET effacer la réponse « défaut ».
  if (rep.fonctionne === 'non') {
    const retourEtape1 = () => { setRep({}); setEtape(0); };
    return (
      <div className="no-scrollbar anim-fade" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
        <EcranHeader titre="" onBack={retourEtape1} />
        <div style={{ padding: '8px 22px 0', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}><Sticker nom="mains-coeur" size={92} anim="pop" /></div>
          <h2 className="t-display" style={{ fontSize: 26, margin: '14px 0 0' }}>On ne le reprend pas en crédit</h2>
          <p style={{ fontSize: 13.5, color: 'var(--stone)', margin: '8px 0 0', lineHeight: 1.6 }}>
            Pour être repris en crédit, il doit être en parfait état. Là il a un défaut, donc
            pas de crédit. Mais ne le jette pas : tu peux nous en faire don, on le recycle proprement.
          </p>
        </div>
        <div style={{ padding: '20px 18px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-block" onClick={() => go('don')}>Faire un don à la place</button>
          <button className="btn btn-tertiary btn-block" onClick={retourEtape1}>Revenir en arrière</button>
        </div>
      </div>
    );
  }

  const fini = etape >= etapes.length;

  if (fini) {
    const etatDeclare = etatDepuisReponses(rep);
    const incomplet = rep.access === 'incomplet';
    // Fiche provisoire (créée par le client, hors Shopify) : crédit sur le prix neuf,
    // même règle que le recalcul serveur (demande.js).
    // Boost Prio (modèle recherché) : lu sur le produit par estimerPourEtat, comme
    // le recalcul serveur ; les fiches provisoires n'en ont jamais.
    const estim = produit.provisoire
      ? (({ credit, eligibleReprise }) => ({ credit, eligible: eligibleReprise, variant: null }))(
        creditFicheProvisoire({ prixNeuf: produit.prixNeuf, accessoiresIncomplets: incomplet }))
      : estimerPourEtat(produit, etatDeclare, {
        accessoiresIncomplets: incomplet,
        boostFidelite: 1, // le boost fidélité réel s'appliquera côté lab à la validation
      });
    const montant = estim.credit;
    const boost = estim.boost || 0;
    const sansBoost = boost > 0 ? estim.creditSansBoost : 0;
    const fin = boost > 0 ? finPrio(produit) : null;
    const sousSeuil = !estim.eligible;
    // Virement proposé si le reste du plafond du mois couvre aussi les virements déjà au panier.
    const montantVir = montantVirement(montant, pctVir);
    const dejaVir = panier.filter((l) => l.ligneId !== remplace && l.paiement === 'virement')
      .reduce((s, l) => s + (l.montantVirement || 0), 0);
    const offreVir = montantVir > 0 && Boolean(dispo?.disponible) && dispo.reste >= dejaVir + montantVir;
    const enVirement = offreVir && paiement === 'virement';

    // Article inéligible à la reprise (prix de revente sous le minimum) →
    // on oriente vers le don (cf. MODELE-PRIX-REPRISE.md).
    if (sousSeuil) {
      return (
        <div className="no-scrollbar anim-fade" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
          <EcranHeader titre="" onBack={() => setEtape(etapes.length - 1)} />
          <div style={{ padding: '8px 22px 0', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Sticker nom="mains-coeur" size={92} anim="pop" /></div>
            <h2 className="t-display" style={{ fontSize: 24, margin: '14px 0 0' }}>Le crédit serait trop faible</h2>
            <p style={{ fontSize: 13.5, color: 'var(--stone)', margin: '8px 0 0', lineHeight: 1.6 }}>
              Seul, ce modèle dans cet état ne vaut pas une reprise.
              Propose-le dans un lot, seul ou avec tes autres jouets : on l’évalue à la main.
              Sinon, le plus utile : nous en faire don.
            </p>
          </div>
          <div style={{ padding: '20px 18px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="btn btn-primary btn-block" onClick={() => go('don')}>Faire un don</button>
            <button className="btn btn-secondary btn-block" onClick={() => go('lot')}>Proposer un lot</button>
            <button className="btn btn-tertiary btn-block" onClick={() => setEtape(0)}>Refaire l’estimation</button>
          </div>
        </div>
      );
    }

    return (
      <div className="no-scrollbar anim-fade" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
        <EcranHeader titre="" onBack={() => setEtape(etapes.length - 1)} />
        <Parcours etape={0} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 22px 0', textAlign: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Thumb produit={produit} size={96} radius={22} />
            <Sticker nom="coeur-etincelle" size={46} rot={12} anim="pop" style={{ position: 'absolute', right: -26, top: -18 }} />
          </div>
          <span className="badge badge-rose" style={{ marginTop: 16 }}>{etatDeclare}</span>
          {offreVir ? <SelecteurPaiement paiement={paiement} setPaiement={setPaiement} montant={montant} montantVir={montantVir} /> : null}
          <div className="t-overline" style={{ marginTop: 14 }}>{enVirement ? 'Ton virement' : 'Ton crédit de reprise'}</div>
          <div key={enVirement ? 'v' : 'b'} className="anim-fade" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 10, marginTop: 4 }}>
            {!enVirement && sansBoost && sansBoost < montant ? <PrixBarre montant={sansBoost} style={{ fontSize: 22 }} /> : null}
            <h2 className="t-display" style={{ fontSize: 44, margin: 0, color: 'var(--rouge)' }}>{fmtEuro(enVirement ? montantVir : montant)}</h2>
          </div>
          {enVirement ? (
            <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 4 }}>
              versés sur ton compte bancaire · ou {fmtEuro(montant)} en bon d’achat
            </div>
          ) : offreVir ? (
            <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 4 }}>
              en bon d’achat rejouis.fr · ou {fmtEuro(montantVir)} par virement
            </div>
          ) : null}
          <PastilleBoost boost={boost} fin={fin} style={{ position: 'relative', marginTop: 6 }} />
          {boost > 0 ? (
            <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 6 }}>
              Modèle recherché : {enVirement ? 'ton offre est boostée' : 'ton crédit est boosté'} {fin ? 'jusqu’au ' + dateLongue(fin) : 'pour une durée limitée'}.
            </div>
          ) : null}
          <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 6, lineHeight: 1.6 }}>
            {produit.titre}{ETAT_DESC[etatDeclare] ? ' · ' + ETAT_DESC[etatDeclare] : ''}
            {incomplet ? <><br /><span style={{ fontSize: 11 }}>accessoires manquants pris en compte</span></> : null}
          </div>
          <div style={{ display: 'flex', gap: 10, padding: '12px 14px', marginTop: 18, fontSize: 12, color: 'var(--encre-doux)', textAlign: 'left', alignItems: 'center',
            background: 'var(--bleu-pale)', borderRadius: 'var(--r-card)', lineHeight: 1.5 }}>
            <Illus nom="bouclier" size={34} />
            {enVirement
              ? 'Ce virement t’est garanti si ton jouet correspond à ta déclaration. On vérifie tout au labo, puis on l’envoie sur ton compte. Ton IBAN te sera demandé avant l’envoi de ta demande.'
              : 'Ce crédit t’est garanti si ton jouet correspond à ta déclaration. On vérifie tout au labo : crédit confirmé 2 jours après réception.'}
          </div>
        </div>
        <div style={{ padding: '18px 18px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-block" onClick={() => onAjouter({
            produitId: produit.id,
            variantId: estim.variant?.id || null,
            ficheRef: produit.provisoire ? produit.ref : null,
            handle: produit.handle,
            titre: produit.titre,
            marque: produit.marque,
            image: produit.image,
            etatDeclare,
            accessoiresIncomplets: incomplet,
            credit: montant,
            creditSansBoost: sansBoost || null,
            boostPrio: boost,
            ...(enVirement ? { paiement: 'virement', montantVirement: montantVir } : {}),
            produit, // conservé pour « modifier l'état » depuis le panier
            remplace, // « Modifier l'état » : remplace la ligne d'origine
          })}>
            Ajouter au panier · {enVirement ? `${fmtEuro(montantVir)} par virement` : fmtEuro(montant)}
          </button>
          <button className="btn btn-tertiary btn-block" onClick={() => setEtape(0)}>Refaire l’estimation</button>
        </div>
      </div>
    );
  }

  function repondre(cle, v) {
    setRep((r) => ({ ...r, [cle]: v }));
    setEtape((e) => e + 1);
  }

  const e = etapes[Math.min(etape, etapes.length - 1)];
  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
      <EcranHeader titre="" onBack={() => (etape === 0 ? go('catalogue', { focus: true }) : setEtape(etape - 1))}
        droite={<span style={{ fontSize: 12, color: 'var(--stone)' }}>{Math.min(etape + 1, etapes.length)}/{etapes.length}</span>} />
      <Parcours etape={0} />
      <div style={{ padding: '0 18px' }}>
        <Progres total={etapes.length} courant={etape} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 4px' }}>
          <Thumb produit={produit} size={40} radius={10} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{produit.titre}</div>
            <div style={{ fontSize: 11, color: 'var(--stone)' }}>{[produit.marque, produit.matiere].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
        <h2 className="t-display" style={{ fontSize: 25, margin: '16px 0 4px' }}>{e.q}</h2>
        <p style={{ fontSize: 13, color: 'var(--stone)', margin: '0 0 16px', lineHeight: 1.55 }}>{e.s}</p>
        <div key={e.cle} className="anim-pop" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {e.reponses.map((r) => (
            <button key={r.v} className="rep-btn" onClick={() => repondre(e.cle, r.v)}>
              {r.ic ? <Illus nom={r.ic} size={40} /> : null}
              <span style={{ flex: 1 }}>
                {r.t}
                {r.s ? <span className="rep-sub">{r.s}</span> : null}
              </span>
              <Icons.chev size={16} style={{ color: 'var(--stone)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// Sélecteur bon d'achat / virement, collé au prix (modèles activés au lab). Le montant,
// les textes et le bouton suivent le choix ; bon d'achat par défaut.
function SelecteurPaiement({ paiement, setPaiement, montant, montantVir }) {
  const option = (cle, titre, m) => {
    const actif = paiement === cle;
    return (
      <button type="button" onClick={() => setPaiement(cle)} aria-pressed={actif}
        style={{ flex: 1, padding: '8px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
          background: actif ? 'var(--noir)' : 'transparent', color: actif ? '#fff' : 'var(--noir)',
          transition: 'background .18s ease, color .18s ease', lineHeight: 1.2 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 13 }}>{titre}</span>
        <span style={{ display: 'block', fontSize: 11.5, opacity: actif ? 0.85 : 0.6, marginTop: 1 }}>{fmtEuro(m)}</span>
      </button>
    );
  };
  return (
    <div style={{ marginTop: 14, width: '100%', maxWidth: 320 }}>
      <div role="group" aria-label="Mode de paiement"
        style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 999, background: 'var(--creme-fonce)', border: '1.5px solid var(--bord)' }}>
        {option('bon', 'Bon d’achat', montant)}
        {option('virement', 'Virement', montantVir)}
      </div>
    </div>
  );
}
